import { useState } from 'react'
import { ChevronRight, Copy, X } from 'lucide-react'
import { DebugEntry } from '../aiChatTypes'
import styles from './DebugDetailModal.module.css'

function tryFormatJson(text: string): string {
  const t = text.trim()
  if (!t.startsWith('{') && !t.startsWith('[')) return text
  try { return JSON.stringify(JSON.parse(t), null, 2) } catch { return text }
}

function MessageCard({ msg, index }: { msg: { role: string; content: string }; index: number }) {
  const [expanded, setExpanded] = useState(false)
  const isCtx = msg.content.startsWith('[SERVER CONTEXT:')
  const label = isCtx ? 'Server Context' : msg.role === 'assistant' ? 'Assistant' : 'User'
  const badgeClass = isCtx ? styles.badgeCtx : msg.role === 'assistant' ? styles.badgeAssistant : styles.badgeUser
  const LIMIT = 800
  const long = msg.content.length > LIMIT
  const display = long && !expanded ? msg.content.slice(0, LIMIT) + '…' : msg.content

  return (
    <div className={styles.msgCard}>
      <div className={styles.msgCardHeader}>
        <span className={`${styles.badge} ${badgeClass}`}>{label}</span>
        <span className={styles.msgIndex}>#{index + 1}</span>
      </div>
      <pre className={styles.msgContent}>{display}</pre>
      {long && (
        <button className={styles.showMoreBtn} onClick={() => setExpanded(v => !v)}>
          {expanded ? 'Show less' : `Show ${msg.content.length - LIMIT} more chars`}
        </button>
      )}
    </div>
  )
}

function CollapsibleSection({ label, defaultOpen = false, badge, children, onCopy }: {
  label: string
  defaultOpen?: boolean
  badge?: string
  children: React.ReactNode
  onCopy?: () => void
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className={styles.section}>
      <button className={styles.sectionHeader} onClick={() => setOpen(v => !v)}>
        <ChevronRight size={12} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} />
        <span className={styles.sectionLabel}>{label}</span>
        {badge && <span className={styles.sectionBadge}>{badge}</span>}
        {onCopy && (
          <button className={styles.copyBtn} onClick={e => { e.stopPropagation(); onCopy() }}>
            <Copy size={11} />
          </button>
        )}
      </button>
      {open && <div className={styles.sectionBody}>{children}</div>}
    </div>
  )
}

export default function DebugDetailModal({ entry, previousMessageCount = 0, onClose }: {
  entry: DebugEntry
  previousMessageCount?: number
  onClose: () => void
}) {
  const copy = (text: string) => { navigator.clipboard.writeText(text).catch(() => {}) }
  const [showEarlier, setShowEarlier] = useState(false)

  const prevCount = Math.min(Math.max(previousMessageCount, 0), entry.messages.length)
  const earlierMessages = entry.messages.slice(0, prevCount)
  const newMessages = entry.messages.slice(prevCount)
  // The system prompt is identical on every call, so only surface it on the first turn.
  const isFirstTurn = prevCount === 0

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <span className={styles.title}>Debug Info</span>
          <button className={styles.closeBtn} onClick={onClose}><X size={15} /></button>
        </div>

        <div className={styles.body}>
          {/* AI Instructions (system prompt) — identical every turn, shown only on the first */}
          {isFirstTurn && (
            <CollapsibleSection
              label="AI Instructions"
              defaultOpen={false}
              onCopy={() => copy(entry.systemPrompt)}
            >
              <pre className={styles.block}>{entry.systemPrompt}</pre>
            </CollapsibleSection>
          )}

          {/* Messages — new-for-this-turn shown first, earlier context collapsed */}
          <CollapsibleSection
            label="Messages"
            defaultOpen={true}
            badge={prevCount > 0 ? `${newMessages.length} new` : String(entry.messages.length)}
            onCopy={() => copy(JSON.stringify(entry.messages, null, 2))}
          >
            <div className={styles.msgList}>
              {earlierMessages.length > 0 && (
                <>
                  <button className={styles.earlierToggle} onClick={() => setShowEarlier(v => !v)}>
                    <ChevronRight size={11} className={`${styles.chevron} ${showEarlier ? styles.chevronOpen : ''}`} />
                    {showEarlier
                      ? 'Hide earlier context'
                      : `Show ${earlierMessages.length} earlier ${earlierMessages.length === 1 ? 'message' : 'messages'}`}
                  </button>
                  {showEarlier && earlierMessages.map((msg, i) => <MessageCard key={`e${i}`} msg={msg} index={i} />)}
                </>
              )}
              {newMessages.map((msg, i) => <MessageCard key={`n${i}`} msg={msg} index={prevCount + i} />)}
              {entry.messages.length === 0 && <span className={styles.empty}>No messages</span>}
            </div>
          </CollapsibleSection>

          {/* AI Thinking — only when thinking content exists */}
          {entry.thinking && (
            <CollapsibleSection
              label="AI Thinking"
              defaultOpen={true}
              onCopy={() => copy(entry.thinking)}
            >
              <pre className={`${styles.block} ${styles.thinkingBlock}`}>{entry.thinking}</pre>
            </CollapsibleSection>
          )}

          {/* Response */}
          <CollapsibleSection
            label="Response"
            defaultOpen={true}
            onCopy={() => copy(entry.response)}
          >
            <pre className={styles.block}>{tryFormatJson(entry.response)}</pre>
          </CollapsibleSection>
        </div>
      </div>
    </div>
  )
}
